import React, { useState, useEffect, useCallback } from 'react';
import api from "../../services/api";
import Datepicker from "react-tailwindcss-datepicker";
import Select from "react-select";
import * as XLSX from "xlsx";
import { BrandsTypes } from '../../utils/constants';
import "../creatives/CreativeList.scss";

const formatDate = (date) => new Date(date).toISOString()?.split('T')[0];

const SalesWiseInteractions = () => {
    const today = formatDate(new Date());
    const [value, setValue] = useState({ startDate: today, endDate: today });
    const [selectedUserType, setSelectedUserType] = useState({ value: 1, label: "National" });
    const [results, setResults] = useState({ data: [], interactions: [] });

    const handleValueChange = (newValue) => setValue(newValue);

    const getData = useCallback(async () => {
        if (!selectedUserType || !value.startDate || !value.endDate) {
            alert("Please select a brand type and date range.");
            return;
        }

        try {
            const res = await api({
                url: "/effort_analysis/get_interactions",
                method: "POST",
                responseType: "json",
                data: {
                    startDate: value.startDate,
                    endDate: value.endDate,
                    new_user_type: selectedUserType.value,
                },
            });
            setResults(res.data || { data: [], interactions: [] });
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [selectedUserType, value]);

    useEffect(() => {
        getData();
    }, [getData]);

    const calculatePersonStats = (person) => {
        const personInteractions = results.interactions.filter(
            (int) => int.sales_person_id === person.sales_person_id
        );

        const direct = personInteractions
            .filter((int) => int.type <= 2)
            .reduce((acc, curr) => acc + parseInt(curr.interaction_count), 0);

        const indirect = personInteractions
            .filter((int) => int.type > 2)
            .reduce((acc, curr) => acc + parseInt(curr.interaction_count), 0);

        return {
            direct,
            indirect,
            total: direct + indirect
        };
    };

    const handleExport = () => {
        const headers = [["Sales Person", "Known", "Unknown", "Missed", "Total", "Direct", "Indirect"]];
        const rows = results.data.map((person) => {
            const { direct, indirect, total } = calculatePersonStats(person);

            return [
                person.sales_person_name,
                parseInt(person.known),
                parseInt(person.unknown),
                parseInt(person.missed),
                total,
                direct,
                indirect
            ];
        });

        const totalRow = [
            "Total",
            results?.data?.reduce((sum, p) => sum + parseInt(p.known), 0) || 0,
            results?.data?.reduce((sum, p) => sum + parseInt(p.unknown), 0) || 0,
            results?.data?.reduce((sum, p) => sum + parseInt(p.missed), 0) || 0,
            results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).total, 0) || 0,
            results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).direct, 0) || 0,
            results?.data?.reduce((sum, p) => sum + calculatePersonStats(p).indirect, 0 || 0)
        ];

        const worksheet = XLSX.utils.aoa_to_sheet([...headers, ...rows, totalRow]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Impressions");

        XLSX.writeFile(workbook, `sales_impressions_${value.startDate}_to_${value.endDate}.csv`);
    };

    const totals = (results?.data || []).reduce(
        (acc, p) => {
            const s = calculatePersonStats(p);
            return {
                known: acc.known + (parseInt(p.known) || 0),
                unknown: acc.unknown + (parseInt(p.unknown) || 0),
                missed: acc.missed + (parseInt(p.missed) || 0),
                total: acc.total + s.total,
                direct: acc.direct + s.direct,
                indirect: acc.indirect + s.indirect,
            };
        },
        { known: 0, unknown: 0, missed: 0, total: 0, direct: 0, indirect: 0 }
    );

    const totalPills = [
        { label: "Known", value: totals.known },
        { label: "Unknown", value: totals.unknown },
        { label: "Missed", value: totals.missed },
        { label: "Interactions", value: totals.total },
        { label: "Direct", value: totals.direct },
        { label: "Indirect", value: totals.indirect },
    ];

    return (
        <div className="creativeListPage flex flex-col h-[calc(100vh-72px)]">
            <div className="cl-card flex min-h-0 flex-1 flex-col">
                <header className="cl-header shrink-0">
                    <div className="cl-title-block">
                        <h1 className="cl-title">Sales-wise Interactions</h1>
                        <p className="cl-subtitle">{results?.data?.length || 0} sales people</p>
                    </div>
                    <div className="cl-toolbar cl-toolbar--inline">
                        <div className="flex w-full flex-col gap-1 sm:w-auto">
                            <label className="text-xs font-medium text-slate-500">Date range</label>
                            <div className="cl-daterange w-full sm:min-w-[240px]">
                                <Datepicker
                                    value={value}
                                    primaryColor="indigo"
                                    showShortcuts
                                    configs={{
                                        shortcuts: {
                                            today: "today",
                                            yesterday: "yesterday",
                                            currentMonth: "currentMonth",
                                            pastMonth: "lastMonth",
                                        },
                                    }}
                                    onChange={handleValueChange}
                                />
                            </div>
                        </div>
                        <div className="flex w-full flex-col gap-1 sm:w-48">
                            <label className="text-xs font-medium text-slate-500">Brand type</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={BrandsTypes.filter((option) => option.value !== 5)}
                                value={selectedUserType}
                                onChange={setSelectedUserType}
                            />
                        </div>
                        <button onClick={handleExport} className="cl-download-btn w-full sm:w-auto sm:self-end">
                            <sl-icon library="lucide" name="download"></sl-icon>
                            Download CSV
                        </button>
                    </div>
                </header>

                <div className="cl-table-wrap flex-1 min-h-0 overflow-y-auto">
                    <table className="cl-table cl-table--multi cl-table--grouped">
                        <thead>
                            <tr>
                                <th rowSpan="2">Sales Person</th>
                                <th rowSpan="2">Known</th>
                                <th rowSpan="2">Unknown</th>
                                <th rowSpan="2">Missed</th>
                                <th colSpan="3" style={{ textAlign: "center" }}>Interactions</th>
                            </tr>
                            <tr>
                                <th>Total</th>
                                <th>Direct</th>
                                <th>Indirect</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results?.data?.length > 0 ? (
                                results.data.map((person) => {
                                    const { direct, indirect, total } = calculatePersonStats(person);

                                    return (
                                        <tr key={person.sales_person_id}>
                                            <td className="cl-name" data-label="Sales Person">{person.sales_person_name}</td>
                                            <td data-label="Known">{parseInt(person.known)}</td>
                                            <td data-label="Unknown">{parseInt(person.unknown)}</td>
                                            <td data-label="Missed">{parseInt(person.missed)}</td>
                                            <td data-label="Total" className="font-semibold text-slate-800">{total}</td>
                                            <td data-label="Direct">{direct}</td>
                                            <td data-label="Indirect">{indirect}</td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="7" className="text-center text-gray-500 py-6">
                                        No data available.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Totals — always visible (outside the scroll area) */}
                <div className="shrink-0 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-200 bg-white px-4 py-2.5 shadow-[0_-2px_8px_rgba(16,24,40,0.04)]">
                    <span className="text-sm font-bold text-slate-800">Totals</span>
                    {totalPills.map((s) => (
                        <span key={s.label} className="inline-flex items-baseline gap-1.5 rounded-lg bg-slate-50 px-2.5 py-1">
                            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{s.label}</span>
                            <span className="text-sm font-bold tabular-nums text-slate-800">{s.value}</span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default SalesWiseInteractions;
